import * as React from 'react';
import { Table } from 'reactstrap';

const workflowsStyles = require('./workflows.css');

/** Windowed page numbers around the current page (mock-style numbered pagination). */
const getPageNumbers = (pageIndex: number, pageCount: number) => {
  const MAX_VISIBLE = 7;
  if (pageCount <= MAX_VISIBLE) {
    return Array.from({ length: pageCount }, (_, i) => i);
  }

  const start = Math.max(
    0,
    Math.min(pageIndex - 3, pageCount - MAX_VISIBLE)
  );
  return Array.from({ length: MAX_VISIBLE }, (_, i) => start + i);
};

export const WorkflowsTable = ({
  state,
  getTableProps,
  headerGroups,
  getTableBodyProps,
  rows,
  prepareRow,
  pageOptions,
  pageCount,
  page,
  gotoPage,
  previousPage,
  nextPage,
  canPreviousPage,
  canNextPage,
  setPageSize,
  handleRowClick,
  showFilters,
}) => {
  const pageIndex = state.pageIndex;
  const pageSize = state.pageSize;

  const firstRow = rows.length === 0 ? 0 : pageIndex * pageSize + 1;
  const lastRow = Math.min((pageIndex + 1) * pageSize, rows.length);

  return (
    <div className={workflowsStyles.fullHeight}>
      <div className={workflowsStyles.fullHeightTableContainer}>
        <div className={workflowsStyles.fitContentTableContainer}>
          <Table
            {...getTableProps()}
            className={workflowsStyles.workflowsTable}
          >
            <thead>
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => {
                    const isDescription = column.id === 'description';
                    const headerProps = column.getHeaderProps(
                      column.getSortByToggleProps()
                    );
                    return (
                      <th
                        {...headerProps}
                        className={[
                          workflowsStyles.tableHeaderCell,
                          isDescription
                            ? workflowsStyles.descriptionColumn
                            : '',
                          headerProps.className,
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      >
                        <span className={workflowsStyles.headerLabel}>
                          {column.render('Header')}
                          {column.canSort && (
                            <span
                              className={`${workflowsStyles.sortIndicator} ${
                                column.isSorted
                                  ? workflowsStyles.sortIndicatorActive
                                  : ''
                              }`}
                            >
                              {column.isSorted
                                ? column.isSortedDesc
                                  ? '▼'
                                  : '▲'
                                : '↕'}
                            </span>
                          )}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              ))}
              {showFilters &&
                headerGroups.map((headerGroup) => (
                  <tr
                    {...headerGroup.getHeaderGroupProps()}
                    key={`filters-${headerGroup.id}`}
                    className={workflowsStyles.filterRow}
                  >
                    {headerGroup.headers.map((column) => {
                      const isDescription = column.id === 'description';
                      return (
                        <th
                          {...column.getHeaderProps()}
                          className={[
                            workflowsStyles.tableFilterCell,
                            isDescription
                              ? workflowsStyles.descriptionColumn
                              : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                        >
                          {column.canFilter ? column.render('Filter') : null}
                        </th>
                      );
                    })}
                  </tr>
                ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row) => {
                prepareRow(row);
                return (
                  <tr
                    {...row.getRowProps()}
                    className={workflowsStyles.selectableRow}
                  >
                    {row.cells.map((cell) => {
                      const isDescription = cell.column.id === 'description';
                      const cellProps = cell.getCellProps({
                        onClick: (e) =>
                          handleRowClick && handleRowClick(row, cell, e),
                      });
                      return (
                        <td
                          {...cellProps}
                          className={[
                            workflowsStyles.tableBodyCell,
                            isDescription
                              ? workflowsStyles.descriptionColumn
                              : '',
                            cellProps.className,
                          ]
                            .filter(Boolean)
                            .join(' ')}
                        >
                          {cell.render('Cell')}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
        <div className={workflowsStyles.footerBar}>
          <div>
            {`Showing ${firstRow} to ${lastRow} of ${rows.length} workflows`}
          </div>
          <div className={workflowsStyles.pageButtons}>
            <button
              className={workflowsStyles.pageBtn}
              onClick={() => previousPage()}
              disabled={!canPreviousPage}
            >
              ‹
            </button>
            {getPageNumbers(pageIndex, pageCount).map((pageNumber) => (
              <button
                key={pageNumber}
                className={`${workflowsStyles.pageBtn} ${
                  pageNumber === pageIndex ? workflowsStyles.pageBtnActive : ''
                }`}
                onClick={() => gotoPage(pageNumber)}
              >
                {pageNumber + 1}
              </button>
            ))}
            <button
              className={workflowsStyles.pageBtn}
              onClick={() => nextPage()}
              disabled={!canNextPage}
            >
              ›
            </button>
            <select
              className={workflowsStyles.pageSizeSelect}
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
            >
              {[10, 20, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {`${size} / page`}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};
